import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { fadeIn, slideUp } from '../styles/animations';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.3s ease;
  z-index: 1000;
`;

const DialogBox = styled.div`
  background-color: ${({ theme }) => theme.body || 'white'};
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 25px rgba(0,0,0,0.3);
  animation: ${slideUp} 0.4s ease-out;
  text-align: center;
  font-family: ${({ theme }) => theme.fontFamily || 'inherit'};
  border: 2px solid ${({ theme }) => theme.controlsBorder || 'rgba(255,255,255,0.2)'};
`;

const Message = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.text || '#333'};
  margin-bottom: 20px;
  font-family: ${({ theme }) => theme.fontFamily || 'inherit'};
`;

const Input = styled.input`
  font-family: ${({ theme }) => theme.fontFamily || 'inherit'};
  font-size: 16px;
  padding: 12px;
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  border: 3px solid ${({ theme }) => theme.controlsBorder || '#8B4513'};
  background-color: ${({ theme }) => theme.body || '#fff'};
  color: ${({ theme }) => theme.text || '#333'};
  text-align: center;
  margin-bottom: 25px;
  box-sizing: border-box;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.tilePlayer || '#E57A44'};
    box-shadow: 0 0 0 3px rgba(229, 122, 68, 0.2);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const Button = styled.button`
  padding: 12px 25px;
  font-size: 16px;
  border-radius: 5px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.fontFamily || 'inherit'};

  &.confirm {
    background-color: ${({ theme }) => theme.buttonColor || '#28a745'};
    color: white;
    &:hover {
      opacity: 0.9;
      transform: scale(1.05);
    }
  }

  &.cancel {
    background-color: ${({ theme }) => theme.controlsBorder || '#dc3545'};
    color: white;
    &:hover {
      opacity: 0.9;
      transform: scale(1.05);
    }
  }
`;

const NameInputDialog = ({ message, initialValue, onConfirm, onCancel, theme }) => {
  const [inputValue, setInputValue] = useState(initialValue || '');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus input when dialog opens
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleConfirm = () => {
    const trimmed = inputValue.trim();
    if (trimmed.length > 0 && trimmed.length <= 20) {
      onConfirm(trimmed);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <DialogOverlay onClick={onCancel}>
      <DialogBox theme={theme} onClick={(e) => e.stopPropagation()}>
        <Message theme={theme}>{message}</Message>
        <Input
          ref={inputRef}
          theme={theme}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          maxLength={20}
          placeholder="Enter name"
        />
        <ButtonContainer>
          <Button className="confirm" theme={theme} onClick={handleConfirm}>
            Confirm
          </Button>
          <Button className="cancel" theme={theme} onClick={onCancel}>
            Cancel
          </Button>
        </ButtonContainer>
      </DialogBox>
    </DialogOverlay>
  );
};

export default NameInputDialog;

